import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Icon } from "@iconify/react";
import arrowFill from "@iconify/icons-eva/arrow-back-fill";
import flashFill from "@iconify/icons-eva/flash-fill";
import downloadFill from "@iconify/icons-eva/download-fill";
import edit2Fill from "@iconify/icons-eva/edit-2-fill";
import {
  Container,
  Stack,
  Button,
  CircularProgress,
  Typography,
  Chip,
  Grid,
  Paper,
  Box,
  Skeleton,
} from "@material-ui/core";
import Page from "src/components/Page";
import Page404 from "src/pages/Page404";
import Contenido from "src/components/propuesta/verPropuestaV2/Contenido";
import PanelAdmin from "src/components/propuesta/verPropuestaV2/PanelAdmin";
import HiloNegociacion from "src/components/propuesta/verPropuestaV2/HiloNegociacion";
import ModalEnvioPropuestaV2 from "src/components/propuesta/verPropuestaV2/ModalEnvioPropuestaV2";
import { evaluarCompletitudPropuestaV2 } from "src/utils/propuestaV2Completitud";
import { formatValorItem } from "src/utils/propuestasV2/formatValorItem";
import { propuestasApi } from "src/services/propuestasApi";
import { useSnackbar } from "src/components/snackbar";
import { BRAND } from "src/theme/brandTokens";

const ESTADO_CONFIG = {
  BORRADOR: { label: "Borrador", color: "default" },
  PENDIENTE: { label: "Pendiente", color: "info" },
  EN_RENEGOCIACION: { label: "En negociación", color: "primary" },
  ACEPTADA: { label: "Aceptada", color: "success" },
  PARCIALMENTE_ACEPTADA: { label: "Parcialmente aceptada", color: "warning" },
  RECHAZADA: { label: "Rechazada", color: "error" },
  CADUCADA: { label: "Caducada", color: "default" },
};

export default function VerPropuestaV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: propuesta,
    isLoading,
    refetch,
  } = propuestasApi.useGetPropuestaV2Query({ id });

  const [descargarPDFMutation, { isLoading: descargandoPDF }] =
    propuestasApi.useDescargarPDFPropuestaV2Mutation();

  // ── Estado del modal de envío
  const [modalEnvioAbierto, setModalEnvioAbierto] = useState(false);
  const [modoForzado, setModoForzado] = useState(undefined);
  const [criticalOverride, setCriticalOverride] = useState(null);

  const [enviarPropuesta, { isLoading: enviando }] =
    propuestasApi.useEnviarPropuestaClienteMutation();
  const [reenviarPropuesta, { isLoading: reenviando }] =
    propuestasApi.useReenviarPropuestaClienteMutation();
  const cargandoEnvio = enviando || reenviando;

  const abrirEnvio = () => {
    setModoForzado(undefined);
    setCriticalOverride(null);
    setModalEnvioAbierto(true);
  };

  const abrirReenvio = () => {
    setModoForzado("reenvio");
    setCriticalOverride(null);
    setModalEnvioAbierto(true);
  };

  const handleConfirmarEnvio = async (mensaje, esReenvio) => {
    try {
      const mutation = esReenvio ? reenviarPropuesta : enviarPropuesta;
      await mutation({ id: propuesta.id, message: mensaje }).unwrap();
      enqueueSnackbar(
        esReenvio ? "Propuesta reenviada." : "Propuesta enviada.",
        { variant: "success" },
      );
      setModalEnvioAbierto(false);
      refetch();
    } catch (err) {
      if (err?.data?.critical) {
        // El modo bloqueado por completitud necesita el modal abierto: es
        // ahí donde se muestra la lista de requisitos faltantes.
        setCriticalOverride(err.data.critical);
      } else {
        enqueueSnackbar(err?.data?.message || "Error al enviar la propuesta.", {
          variant: "error",
        });
        // Un fallo genérico (por ejemplo, el correo) ya no tiene nada más
        // que resolver en este modal: cerrarlo evita que el vendedor pulse
        // de nuevo un botón que quedó desincronizado con el estado real.
        setModalEnvioAbierto(false);
      }
      // El envío del correo corre fuera de la transacción: un fallo de
      // Nodemailer llega acá con la ronda ya escrita, el documento archivado
      // y el estado ya cambiado en el servidor. Sin este refetch la pantalla
      // sigue mostrando el estado anterior con el botón de enviar, y pulsarlo
      // de nuevo choca contra "solo se puede enviar en BORRADOR" — justo el
      // error que el mensaje de arriba le pide evitar.
      refetch();
    }
  };

  const descargarPDF = async () => {
    try {
      const blob = await descargarPDFMutation({ id }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `propuesta-${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar("Error al generar el PDF.", { variant: "error" });
    }
  };

  const descargarPDFFirmado = () => {
    if (propuesta?.urlPropuesta) {
      window.open(propuesta.urlPropuesta, "_blank", "noopener,noreferrer");
    }
  };

  if (isLoading) return <LoadingComponent />;
  if (!propuesta) return <Page404 />;

  const estadoConf = ESTADO_CONFIG[propuesta.estado] ?? {
    label: propuesta.estado,
    color: "default",
  };

  const estadoTerminal = [
    "ACEPTADA",
    "PARCIALMENTE_ACEPTADA",
    "RECHAZADA",
  ].includes(propuesta.estado);
  const mostrarPDFFirmado = estadoTerminal && !!propuesta.urlPropuesta;

  const esBorradorV2 = propuesta.estado === "BORRADOR";
  const { critical: erroresCriticos } = esBorradorV2
    ? evaluarCompletitudPropuestaV2(propuesta)
    : { critical: [] };
  const listoParaEnviar = erroresCriticos.length === 0;

  return (
    <Page title="Ver propuesta — TAXTIC">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* ── Barra superior — fila 1: navegación + acciones ─────────────── */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Button
            variant="contained"
            color="info"
            size="small"
            startIcon={<Icon icon={arrowFill} />}
            onClick={() => navigate(-1)}
            sx={{ alignSelf: { xs: "flex-start", sm: "auto" } }}
          >
            Volver
          </Button>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{ rowGap: 1 }}
          >
            {propuesta.estado === "BORRADOR" && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Icon icon={edit2Fill} />}
                onClick={() =>
                  navigate(`/dashboard/propuestas/v2/${id}/editar`)
                }
                sx={{
                  borderColor: BRAND.color.primary,
                  color: BRAND.color.primary,
                }}
              >
                Editar
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={
                descargandoPDF ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <Icon icon={downloadFill} />
                )
              }
              disabled={descargandoPDF}
              onClick={descargarPDF}
              sx={{
                borderColor: BRAND.color.primary,
                color: BRAND.color.primary,
              }}
            >
              {descargandoPDF ? "Generando…" : "Descargar PDF"}
            </Button>
            {mostrarPDFFirmado && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Icon icon={downloadFill} />}
                onClick={descargarPDFFirmado}
                sx={{
                  backgroundColor: BRAND.color.primary,
                  "&:hover": { backgroundColor: BRAND.color.primaryDark },
                }}
              >
                PDF firmado
              </Button>
            )}
          </Stack>
        </Stack>

        {/* ── Barra superior — fila 2: identidad + chips ─────────────────── */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 3 }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "baseline" }}
            spacing={{ xs: 0.5, md: 1 }}
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              Propuesta #{propuesta.id}
            </Typography>
            {propuesta.titulo && (
              <Typography
                variant="h6"
                color="textSecondary"
                sx={{
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: { xs: "block", md: "-webkit-box" },
                  WebkitLineClamp: { md: 1 },
                  WebkitBoxOrient: "vertical",
                }}
              >
                · {propuesta.titulo}
              </Typography>
            )}
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{ rowGap: 1, flexShrink: 0 }}
          >
            <Chip
              label={estadoConf.label}
              color={estadoConf.color}
              size="small"
              sx={{ fontWeight: 600 }}
            />
            {Boolean(propuesta.generadoConIA) && (
              <Chip
                size="small"
                icon={<Icon icon={flashFill} width={14} />}
                label="Generado con IA"
                sx={{
                  bgcolor: BRAND.color.primaryBg,
                  color: BRAND.color.primary,
                  border: `1px solid ${BRAND.color.primary}`,
                  fontWeight: 600,
                }}
              />
            )}
            {esBorradorV2 && (
              <Chip
                size="small"
                label={
                  listoParaEnviar
                    ? "Listo para enviar"
                    : `Faltan ${erroresCriticos.length} requisitos`
                }
                color={listoParaEnviar ? "success" : "warning"}
                onClick={abrirEnvio}
                sx={{ cursor: "pointer", fontWeight: 600 }}
              />
            )}
          </Stack>
        </Stack>

        {/* ── Layout 2 columnas ───────────────────────────────────────────── */}
        <Grid container spacing={3}>
          {/* Columna izquierda — Contenido espejo del PDF */}
          <Grid item xs={12} md={8}>
            <Contenido propuesta={propuesta} />
          </Grid>

          {/* Columna derecha — Panel administrativo */}
          <Grid item xs={12} md={4}>
            <PanelAdmin
              propuesta={propuesta}
              refetch={refetch}
              onAbrirEnvio={abrirEnvio}
              onAbrirReenvio={abrirReenvio}
            />
          </Grid>
        </Grid>

        {/* ── Servicios retirados durante la negociación ─────────────────── */}
        {Array.isArray(propuesta.itemsRetirados) &&
          propuesta.itemsRetirados.length > 0 && (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mt: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Servicios retirados
              </Typography>
              <Stack spacing={1.5}>
                {propuesta.itemsRetirados.map((item) => (
                  <Stack
                    key={item.id}
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      opacity: 0.75,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, textDecoration: "line-through" }}
                      >
                        {item.faseTitulo ||
                          item.nombreServicio ||
                          `Ítem #${item.id}`}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Fuera de la propuesta
                        {item.retiradoEnRonda != null
                          ? ` · retirado en la ronda ${item.retiradoEnRonda}`
                          : ""}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {formatValorItem(item.tipoMoneda, item.valorConDescuento)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}

        {/* ── Hilo de la negociación ───────────────────────────────────────── */}
        <Box sx={{ mt: 3 }}>
          <HiloNegociacion propuestaId={propuesta.id} />
        </Box>
      </Container>

      {/* ── Modal de envío / reenvío V2 ──────────────────────────────────── */}
      <ModalEnvioPropuestaV2
        abierto={modalEnvioAbierto}
        onCerrar={() => setModalEnvioAbierto(false)}
        propuesta={propuesta}
        onConfirmarEnvio={handleConfirmarEnvio}
        enviando={cargandoEnvio}
        modoForzado={modoForzado}
        criticalOverride={criticalOverride}
      />
    </Page>
  );
}

const LoadingComponent = () => (
  <Page title="Cargando…">
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Skeleton variant="rectangular" width={100} height={36} />
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="rectangular" width={140} height={36} />
      </Stack>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={160} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={240} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Skeleton variant="rectangular" height={400} />
        </Grid>
      </Grid>
    </Container>
  </Page>
);
